// Pixels of clearance left above the scroll target — matches the height of
// the absolutely-positioned SuperChat header that overlays the top of the
// scroll container. Single source of truth shared by useSuperChatScroll
// (scroll offset) and SuperChat styles (scrollContainer paddingTop).
export const HEADER_CLEARANCE = 50;

export const STATUS = {
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    RETRYING: 'retrying',
    ERROR: 'error',
    AWAITING_RESPONSE: 'awaiting-response',
    AWAITING_USER_RESPONSE: 'awaiting-response'
};

export const EVENT_TYPES = {
    CONNECTION: 'connection',
    MESSAGE: 'message',
    HEARTBEAT: 'heartbeat'
};

export const MESSAGE_TYPES = {
    NEW_CONNECTION_REQUEST: 'NEW_CONNECTION_REQUEST',
    USER_QUERY: 'USER_QUERY',
    USER_PING: 'USER_PING',
    USER_QUERY_RESPONSE: 'USER_QUERY_RESPONSE',
    PRODUCTS_RESPONSE: 'PRODUCTS_RESPONSE',
    COMPLEMENTARY_PRODUCTS: 'COMPLEMENTARY_PRODUCTS'
};

export const WS_ENDPOINTS = {
    INTERACTION: 'ws-interaction',
    ASSIST: 'ws-assist'
};

export const MESSAGE_ROLES = {
    USER: 'user',
    ASSISTANT: 'assistant'
};

export const RESPONSE_TYPES = {
    CAMPAIGN_CONFIG_RESPONSE: 'CAMPAIGN_CONFIG_RESPONSE',
    FOLLOW_UP_MESSAGES_RESPONSE: 'FOLLOW_UP_MESSAGES_RESPONSE',
    PRODUCT_RECOMMENDATION_RESPONSE: 'PRODUCTS_RESPONSE',
    PROMOTIONS_RESPONSE: 'PROMOTIONS_RESPONSE',
    PONG_RESPONSE: 'PONG_RESPONSE',
    FEEDBACK_COLLECTION: 'FEEDBACK_COLLECTION',
    CONTEXTUAL_DIVIDER: 'CONTEXTUAL_DIVIDER',
    SHADES_RESPONSE: 'SHADES_RESPONSE',
    COMPLEMENTARY_PRODUCTS_RESPONSE: 'COMPLEMENTARY_PRODUCTS_RESPONSE',
    COMPARISON_RESPONSE: 'COMPARISON_RESPONSE',
    ROUTINE_RESPONSE: 'ROUTINE_RESPONSE',
    ORDER_HISTORY_RESPONSE: 'ORDER_HISTORY_RESPONSE',
    ORDER_DETAILS_RESPONSE: 'ORDER_DETAILS_RESPONSE',
    GLADLY_HANDOFF: 'GLADLY_HANDOFF',
    FAILURE: 'FAILURE',
    USER_QUERY_CHUNK: 'USER_QUERY_CHUNK',
    END_OF_TRANSMISSION: 'END_OF_TRANSMISSION'
};

export const FAILURE_PHASES = {
    SUMMARY: 'summary',
    PRODUCTS: 'products',
    PROMOTIONS: 'promotions',
    FOLLOW_UPS: 'follow_ups'
};

export const CRITICAL_FAILURE_PHASES = [FAILURE_PHASES.SUMMARY];

// Product comparison cardinality guards (BR-004): a comparison must have at
// least 2 and at most 5 products. Frames with fewer than the minimum are
// rejected at the model layer; frames above the maximum are capped at render.
export const MIN_COMPARISON_PRODUCTS = 2;

export const MAX_COMPARISON_PRODUCTS = 5;

// Per Figma (node 2726:57676): each comparison product column is 192px
// (160 content + 16 padding on each side).
export const COMPARISON_COLUMN_WIDTH = 192;

export const VARIANT_CHIP_MAX_WIDTH = 180;

export const VARIANT_CHIP_THUMBNAIL_SIZE = 20;

export const MAX_ROUTINE_STEPS = 6;

export const MAX_ROUTINE_NAME_LENGTH = 43;

export const MAX_LIST_NAME_LENGTH = 45;

export const ROUTINE_ADDED_DURATION = 2000;

export const ROUTINE_THUMBNAIL_LIMIT = 4;

export const ROUTINE_THUMBNAIL_SIZE = 77;

// Order-status (WISMO) card — product avatar geometry (px) and overflow cap.
export const AVATAR_SIZE = 36;

export const AVATAR_IMAGE_SIZE = 28;

export const AVATAR_OVERLAP = 8;

export const MAX_VISIBLE_ITEMS = 4;

export const SHADES_RESPONSE_STEPS = {
    LIST_PRODUCTS: 'list_products',
    LIST_SHADES: 'list_shades'
};

export const ENTRYPOINTS = {
    PDP: 'PDP',
    PLP: 'PLP',
    SRP: 'SRP',
    CMS: 'CMS',
    DEFAULT: 'default'
};

export const ERROR_CODES = {
    ABNORMAL_CLOSURE: 1006
};

export const STD_BASKET_TYPE = 'Standard';

// Locale-stable analytics name for the "Works well with" complementary carousel.
// The displayed header is localized (see ChatComplementaryCarousel locales) and may
// include trailing punctuation, but Adobe Analytics values must stay consistent across
// locales, so tracking uses this fixed English name (and a space-stripped slug).
export const COMPLEMENTARY_CAROUSEL_ANALYTICS_NAME = 'works well with';

// Canonical space-stripped slug for the complementary carousel (e.g. `workswellwith`),
// reused by every analytics builder so the slug rule lives in one place.
export const COMPLEMENTARY_CAROUSEL_ANALYTICS_SLUG = COMPLEMENTARY_CAROUSEL_ANALYTICS_NAME.replace(/\s+/g, '');

export const PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT = 'productRecommendation';

export const PRODUCT_RECOMMENDATION_ANALYTICS_CATEGORY = 'ai-recommendation';

export const BIRTHDAY_REWARD_ANALYTICS_CONTEXT = 'birthdayReward';

export const BIRTHDAY_REWARD_ANALYTICS_CATEGORY = 'ai-birthday-reward';

export const REWARDS_BAZAAR_ANALYTICS_CONTEXT = 'rewardsBazaar';

export const REWARDS_BAZAAR_ANALYTICS_CATEGORY = 'ai-rewards-bazaar';

export const REWARD_TYPES = {
    BIRTHDAY: 'birthday',
    REWARDS_BAZAAR: 'rewardsBazaar'
};

export const REWARD_FALLBACK_URLS = {
    birthday: '/beauty/birthday-gift',
    rewardsBazaar: '/rewards/rewards-bazaar'
};

export const BI_TYPE_BIRTHDAY_GIFT = 'birthday gift';

export const INTENTIONS = {
    GIFT_FINDER: 'GF',
    DEFAULT: 'DEFAULT'
};

export const SENTIMENT_TYPES = {
    PROS: 'pros',
    CONS: 'cons'
};

export const MIN_REVIEWS_FOR_SENTIMENTS = 5;

export const MAX_SENTIMENT_CHIPS = 3;

// GAI-6733: how long a restored "Works well with…" complementary carousel stays valid (48 hours).
export const COMPLEMENTARY_RESTORE_WINDOW_MS = 48 * 60 * 60 * 1000;

export const AB_FEATURES_FLAGS = {
    FRAGRANCE: 'fragrance',
    GIFTS: 'gifts',
    SENTIMENTS: 'sentiments',
    SHADE_FINDER: 'shadeFinder'
};

export const EVENT_DATA_TYPES = {
    USER_HAS_SIGNED_IN: 'USER_HAS_SIGNED_IN_EVENT'
};

// GAI-7427: backend tool intention (snake_case) flagged on the USER_QUERY_RESPONSE
// frame that precedes a shade-finder product recommendation. Used to switch that
// recommendation card's analytics page context to `superchat:shade finder`.
export const SHADE_FINDER_INTENTION = 'shade_finder';

export const SIGN_IN_PATH = '/signin';

export const PROMPT_SOURCES = {
    STATIC: 'static',
    DYNAMIC: 'dynamic'
};
